'use client';

import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  setAppId,
  selectedApplicationData,
  setSelectedApplicationIdData,
  addBreadcrumb,
  setAppetiteData,
  setTotalApplications,
  setCurrentPage,
  setSelectedAppIdName
} from '../../../store/ApplicationSlice';
import { transformAppetiteData } from '../../../utils/generalUtils';
import { dateFilterData } from '../../../config/constant';
import { ApplicationsTable } from '../../../components/application/applications-table';
import { ApplicationSummary } from '../../../components/application/application-summary';
import { Card } from '../../../components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '../../../components/ui/tabs';
import {
  fetchApplicationDataByIdAndName,
  fetchDataForMultipleSelect,
  fetchFilterTotalApplications
} from '../../../api/userService';
import { ApplicationFilters } from '../../../components/application/application-filters';
import { AIGeneratedIndicator } from '../../../components/ui/ai-generated-indicator';
import { Box, CircularProgress } from '@mui/material';
import { DocumentsSection } from '../../application/documents-section';
import { Calendar, Clock, Tag } from 'lucide-react';

export default function ApplicationsPage () {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const applicationData = useSelector(
    state => state.intakeData.selectedApplicationData
  );
  const appetiteData = useSelector(state => state.intakeData.appetiteData);
  const selectedApplicationId = useSelector(
    state => state.intakeData.selectedAppId
  );
  const totalApplications = useSelector(
    state => state.intakeData.totalApplications
  );
  const currentPage = useSelector(state => state.intakeData.currentPage);

  const accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: 'asc'
  });

  const [isLoading, setIsLoading] = useState(false); // State for loader
  const [columnFilters, setColumnFilters] = useState({});
  const [dateFilters, setDateFilters] = useState(dateFilterData);
  const itemsPerPage = 15; // Number of items per page

  const [activeTab, setActiveTab] = useState('details');

  // Build table data
  const tableData = useMemo(
    () => transformAppetiteData(appetiteData),
    [appetiteData]
  );

  useEffect(() => {
    if (tableData.length) {
      dispatch(setSelectedAppIdName(tableData[0]?.customer_name));
      dispatch(setAppId(tableData[0].application_id));
    }
  }, [tableData, dispatch]);

  // Sorting logic
  const sortedData = useMemo(() => {
    if (!sortConfig.column) return tableData;
    return [...tableData].sort((a, b) => {
      if (a[sortConfig.column] < b[sortConfig.column])
        return sortConfig.direction === 'asc' ? -1 : 1;
      if (a[sortConfig.column] > b[sortConfig.column])
        return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [tableData, sortConfig]);

  // Pagination Logic: Get items for the current page
  const paginatedData = useMemo(() => {
    return sortedData.slice(0, itemsPerPage);
  }, [sortedData, itemsPerPage]);

  // Handle Pagination
  const totalPages = useMemo(() => {
    if (!totalApplications || !itemsPerPage) return 1; // Default to 1 page if values are missing
    return Math.ceil(totalApplications / itemsPerPage);
  }, [totalApplications, itemsPerPage]);

  // Fetch application data on application selection change
  useEffect(() => {
    if (!selectedApplicationId) return;

    const fetchSelectedAppData = async () => {
      const customer = paginatedData.find(
        app => app.application_id === selectedApplicationId
      );
      if (customer && customer?.customer_name) {
        const response = await fetchApplicationDataByIdAndName(
          customer.customer_name,
          selectedApplicationId
        );
        dispatch(setSelectedApplicationIdData(response));
      }
    };

    fetchSelectedAppData();
  }, [selectedApplicationId, paginatedData, dispatch]);

  // Event handlers
  const handleSort = column => {
    setSortConfig(prev => ({
      column,
      direction:
        prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleRowClick = (index, id, name) => {
    dispatch(setAppId(id));
    dispatch(setSelectedAppIdName(name));
    dispatch(
      selectedApplicationData(
        appetiteData.find(app => app.application_id === id)
      )
    );
  };

  const handleRowDoubleClick = id => {
    const route = `/app/intake/${id}`;
    if (location.pathname !== route) {
      dispatch(addBreadcrumb({ label: 'Application Overview', path: route }));
    }
    navigate(route);
  };

  // Handle Pagination
  const handlePageChange = async newPage => {
    dispatch(setCurrentPage(newPage));
    await handleUpdateData(newPage);
  };

  const handleUpdateData = async (
    newPage,
    filters = columnFilters,
    dates = dateFilters
  ) => {
    setIsLoading(true);
    const response = await fetchDataForMultipleSelect(
      filters,
      dates,
      accessToken,
      refreshToken,
      newPage
    );
    const totalCount = await fetchFilterTotalApplications(
      filters,
      dates,
      accessToken,
      refreshToken
    );

    if (response) {
      dispatch(setAppetiteData(response)); // Update Redux store
    }
    if (totalCount) {
      dispatch(setTotalApplications(totalCount.total));
    }
    setIsLoading(false);
  };

  const handleMailSourceFilterChange = async selectedValue => {
    setIsLoading(true);
    const filterKey = 'mail_source';
    const updatedFilters = { ...columnFilters, [filterKey]: [selectedValue] };
    setColumnFilters(updatedFilters);
    const response = await fetchDataForMultipleSelect(
      updatedFilters,
      dateFilters,
      accessToken,
      refreshToken
    );
    const totalCount = await fetchFilterTotalApplications(
      updatedFilters,
      dateFilters,
      accessToken,
      refreshToken
    );
    if (response) {
      dispatch(setAppetiteData(response));
    }
    if (totalCount) {
      dispatch(setTotalApplications(totalCount.total));
    }
    dispatch(setCurrentPage(1));
    setIsLoading(false);
  };

  const formatDateUS = dateString => {
    if (!dateString) return '---';
    const date = new Date(dateString);
    if (isNaN(date)) return '---';
    return date.toLocaleDateString('en-US');
  };

  return (
    <div className='min-h-screen bg-gray-50 flex flex-col'>
      {/* <ApplicationHeader /> */}
      {isLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.4)', // Transparent grey background
            zIndex: 9999, // Ensures it overlays everything
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CircularProgress
            size={60}
            sx={{
              color: '#ea580c'
            }}
          />
        </Box>
      )}

      <div className='flex-1 overflow-auto'>
        <div className='p-6 max-w-[1600px] mx-auto'>
          <div className='flex justify-between items-center mb-6'>
            <h1 className='text-2xl font-bold'>Intake Triage and Priority</h1>
            <ApplicationFilters
              setIsLoading={setIsLoading}
              columnFilters={columnFilters}
              dateFilters={dateFilters}
              setDateFilters={setDateFilters}
              setColumnFilters={setColumnFilters}
              handleUpdateData={handleUpdateData}
            />
          </div>

          <Card className='bg-white shadow-md mb-6 overflow-hidden'>
            <ApplicationsTable
              selectedApplicationId={selectedApplicationId}
              sortConfig={sortConfig}
              applications={paginatedData}
              handleSort={handleSort}
              handleRowClick={handleRowClick}
              handleRowDoubleClick={handleRowDoubleClick}
              currPage={currentPage}
              totalPages={totalPages}
              pageSize={itemsPerPage}
              totalItems={totalApplications}
              onPageChange={handlePageChange}
            />
          </Card>

          {selectedApplicationId && (
            <div className='bg-white rounded-lg shadow-md p-5'>
              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                defaultValue='details'
              >
                <div className='flex justify-between items-center mb-4'>
                  <div className='flex justify-between items-start mb-3'>
                    <div className='flex-1'>
                      <h1 className='text-xl font-bold mb-1'>
                        Application #{applicationData.application_id || '---'}{' '}
                        <span
                          className={`px-2 py-0.5 text-sm font-medium rounded-full ${
                            applicationData.application_status ===
                            'under-review'
                              ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                              : applicationData.application_status === 'reject'
                              ? 'bg-red-50 text-red-700 border border-red-200'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {applicationData.application_status || 'Pending'}
                        </span>
                      </h1>
                      <h2 className='text-lg text-gray-700 mb-2'>
                        {applicationData.customer_name || '---'}
                      </h2>
                      <div className='flex items-center gap-4  text-gray-500'>
                        <div className='flex items-center gap-1'>
                          <Calendar className='h-4 w-4' />
                          <span>
                            Submitted:{' '}
                            {formatDateUS(applicationData.mail_received_at) ||
                              '---'}
                          </span>
                        </div>
                        <div className='flex items-center gap-1'>
                          <Clock className='h-4 w-4' />
                          <span>
                            Updated:{' '}
                            {formatDateUS(
                              applicationData.application_last_modified
                            ) || '---'}
                          </span>
                        </div>
                        <div className='flex items-center gap-1'>
                          <Tag className='h-4 w-4' />
                          <span>
                            Priority: {applicationData.priority || '---'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <TabsList className='bg-gray-100'>
                    <TabsTrigger
                      value='details'
                      className='data-[state=active]:bg-[#FFF0ED] data-[state=active]:text-[#FF5533]'
                    >
                      Details
                    </TabsTrigger>
                    <TabsTrigger
                      value='documents'
                      className='data-[state=active]:bg-[#FFF0ED] data-[state=active]:text-[#FF5533]'
                    >
                      Documents
                    </TabsTrigger>
                    {/* <TabsTrigger
                      value="history"
                      className="data-[state=active]:bg-[#FFF0ED] data-[state=active]:text-[#FF5533]"
                    >
                      History
                    </TabsTrigger> */}
                  </TabsList>
                </div>

                <TabsContent value='details'>
                  <ApplicationSummary
                    applicationId={selectedApplicationId}
                    selectedApplicationData={applicationData}
                  />
                </TabsContent>

                <TabsContent value='documents'>
                  <DocumentsSection />
                </TabsContent>

                <TabsContent value='history'>
                  <div className='p-4 border rounded-md'>
                    <h3 className='text-base font-medium mb-4'>
                      Application History
                    </h3>
                    <div className='relative border-l-2 border-gray-200 ml-3 pl-6 space-y-6'>
                      <div className='relative'>
                        <div className='absolute -left-9 top-0 w-5 h-5 rounded-full bg-[#FF5533]'></div>
                        <div>
                          <div className='flex justify-between items-start'>
                            <div>
                              <h4 className=' font-medium'>
                                Application Received
                              </h4>
                              <p className='text-sm text-gray-500'>
                                April 12, 2025 at 9:45 AM
                              </p>
                            </div>
                            <div className='bg-blue-50 text-blue-700 text-sm px-2 py-1 rounded'>
                              System
                            </div>
                          </div>
                          <p className=' text-gray-700 mt-2'>
                            Application #{selectedApplicationId} for Mi
                            Hacienda, LLC has been received.
                          </p>
                        </div>
                      </div>

                      <div className='relative'>
                        <div className='absolute -left-9 top-0 w-5 h-5 rounded-full bg-green-500'></div>
                        <div>
                          <div className='flex justify-between items-start'>
                            <div>
                              <h4 className=' font-medium'>
                                Application Assigned
                              </h4>
                              <p className='text-sm text-gray-500'>
                                April 12, 2025 at 2:15 PM
                              </p>
                            </div>
                            <div className='bg-green-50 text-green-700 text-sm px-2 py-1 rounded'>
                              Assignment
                            </div>
                          </div>
                          <p className=' text-gray-700 mt-2'>
                            Application assigned to John Smith by Maria
                            Rodriguez.
                          </p>
                        </div>
                      </div>

                      <div className='relative'>
                        <div className='absolute -left-9 top-0 w-5 h-5 rounded-full bg-yellow-500'></div>
                        <div>
                          <div className='flex justify-between items-start'>
                            <div>
                              <h4 className=' font-medium'>
                                Information Requested
                              </h4>
                              <p className='text-sm text-gray-500'>
                                April 14, 2025 at 11:15 AM
                              </p>
                            </div>
                            <div className='bg-yellow-50 text-yellow-700 text-sm px-2 py-1 rounded'>
                              Information Request
                            </div>
                          </div>
                          <p className=' text-gray-700 mt-2'>
                            Sarah Johnson requested additional safety
                            documentation from the agent.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </div>

      {/* AI Generated Indicator */}
      <AIGeneratedIndicator />
    </div>
  );
}
